import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  UserImportDataSourceBob,
  UserImportDataSourceStatus,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { useTheme } from '@emotion/react';
import { Button, DialogActions, DialogContent } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { isEqual } from 'lodash';
import { Users } from 'lucide-react';
import { useState } from 'react';
import shortid from 'shortid';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DeleteButton } from '../../../../../../components/DeletionConfirmation';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import {
  removeDoc,
  setDoc,
  updateDoc
} from '../../../../../../services/firebase';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import {
  customFieldToUserImportMapping,
  CustomImportMappingForm2
} from '../CustomImportMappingForm';
import { Img, ImportSourceListItem } from './common';
import { IMPORT_META } from './meta';

const META = IMPORT_META.BOB;

const DEFAULT_MAPPINGS: UserImportMapping[] = [
  {
    dadoKey: 'alternateId',
    otherKey: 'work.employeeIdInCompany'
  },
  {
    dadoKey: 'firstName',
    otherKey: 'firstName'
  },
  {
    dadoKey: 'preferredName',
    otherKey: 'firstName'
  },
  {
    dadoKey: 'lastName',
    otherKey: 'surname'
  },
  {
    dadoKey: 'email',
    otherKey: 'email'
  },
  {
    dadoKey: 'personalEmail',
    otherKey: 'home.privateEmail'
  },
  {
    dadoKey: 'role',
    otherKey: 'work.title'
  },
  {
    dadoKey: 'department',
    otherKey: 'work.department'
  },
  {
    dadoKey: 'location',
    otherKey: 'work.site'
  },
  {
    dadoKey: 'hireDate',
    otherKey: 'work.startDate'
  },
  {
    dadoKey: 'terminationDate',
    otherKey: 'terminationDate'
  },
  {
    dadoKey: 'active',
    otherKey: 'internal.status'
  },
  {
    dadoKey: 'managerId',
    otherKey: 'work.reportsTo.email'
  }
];

export const BobSetupFlow = ({
  orgId,
  onClose,
  customFields
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { id: userId } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const userFields = useUserFields(customFields);

  const [mappings, setMappings] = useState([
    ...DEFAULT_MAPPINGS,
    ...customFields
      .filter((f) => !f.restricted)
      .map((c) => customFieldToUserImportMapping(c))
  ]);

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>

        <CustomImportMappingForm2
          additionalCompositeFields={['$FULL_NAME$']}
          userFields={userFields}
          integrationName="Bob"
          name={t('Default Field Mapping')}
          mappings={mappings}
          onChange={(mappings) => setMappings(mappings)}
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          style={{ marginBottom: theme.spacing(2) }}
          onClick={async () => {
            await setDoc({
              id: shortid(),
              collection: FS.userImportDataSources,
              data: {
                orgId: orgId,
                type: UserImportSourceType.BOB,
                status: UserImportDataSourceStatus.PENDING,
                detailedStatus: '',
                lastUpdate: null,
                createdBy: userId,
                createdAt: now(),
                customMappings: mappings
              }
            });
            onClose();
          }}
        >
          {t('Add soure')}
        </Button>
      </DialogActions>
    </>
  );
};

export const BobExistingSetup = ({
  d,
  onClose,
  customFields
}: {
  d: Doc<UserImportDataSourceBob>;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);

  const [customMappings, setCustomMappings] = useState(d.data.customMappings);

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>
        <FormRow>
          <Alert severity="success" icon={<Users />}>
            {t(
              'bob is currently setup as a user import source. New or updated users in bob will be imported to Dado.'
            )}
          </Alert>
        </FormRow>
        <CustomImportMappingForm2
          additionalCompositeFields={['$FULL_NAME$']}
          userFields={userFields}
          integrationName="Bob"
          name={t('Default Field Mapping')}
          mappings={customMappings}
          onChange={(mappings) => setCustomMappings(mappings)}
        />
      </DialogContent>
      <DialogActionsWithSlots
        left={
          <DeleteButton
            variant="contained"
            color="secondary"
            onDelete={() => removeDoc(d).then(onClose)}
            keyword={d.id}
            dialogTitle={t('Remove integration') + '?'}
            buttonAction={t('Remove integration')}
          >
            {t('Remove integration')}
          </DeleteButton>
        }
        right={
          <>
            <Button onClick={onClose}>{t('Close')}</Button>
            <ButtonWithPromise
              color="primary"
              variant="contained"
              disabled={isEqual(d.data.customMappings, customMappings)}
              pending={`${t('Saving')}...`}
              onClick={() => updateDoc(d, () => ({ customMappings }))}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </>
  );
};

export const ImportSourceRowBob = ({
  d
}: {
  d: Doc<UserImportDataSourceBob>;
}) => {
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      name={META.name}
      logoUrl={META.smallLogoUrl}
    />
  );
};
